import { ORGANISATIONS } from "utils/constants";
import { T_MongoDoc, T_MongoId } from "./_utils.types";

export enum AVAILABLE_CATEGORIES {
  LOAN = "loan",
  BANK = "bank",
  LAWSUIT = "lawsuit",
  AGREEMENT = "agreement",
  OTHER = "other",
}

export enum LOAN_LOCK_FLAGS {
  PENDING = "pending",
  PROCESSING = "processing",
  DONE = "done",
  NONE = "",
}

export enum STATEMENT_FORECAST {
  CASHFLOW = "Cashflow",
  REVENUE = "Revenue",
  EXPENSE = "Expense",
  CATEGORY = "Category",
  OUTLIERS = "Outliers",
}

export enum PAYMENT_STATUS {
  NOT_INITIATED = "not-initiated",
  IN_PROCESS = "processing",
  FAILED = "failed",
  DECLINED = "declined",
  ERROR = "error",
  SUCCESS = "paid",
  NOT_REQUIRED = "not-required",
}

export enum MANAGERS_APPROVAL_STATUS {
  NONE = "none",
  REQUESTED = "requested",
  APPROVED = "approved",
  SUGGESTED = "suggested",
  REJECTED = "rejected",
  CANCELLED = "cancelled",
}
export enum ApplicationType {
  GENERAL = "",
  DROP = "drop",
  AFFILIATE = "affiliate",
}

export type ClientDetails = {
  ssn?: string;
  state?: string;
  city?: string;
  zip_code?: number;
  phone: string;
  street_address?: string;
  status?: string;
  weekly_budget?: number;
  profit_margin?: number;
  name: string;
  alternate_phone?: number;
  cosigners?: {
    name: string;
    email: string;
  }[];
};

export type BusinessInfo = {
  company_name: string;
  ein_number?: string;
  business_type?: string;
  description?: string;
  state?: string;
  city?: string;
  zip_code?: number;
  phone?: string;
  email?: string;
  street_address?: string;
};

export type SelectedBank = {
  bank_name: string;
  account_no: string;
  name_on_account: string;
  routing_no: number;
};

export type MetricDocument = {
  revenue: {
    monthly: number;
    annual: number;
  };
  trueRevenue: {
    monthly: number;
    annual: number;
  };
  expenses: {
    monthly: number;
    annual: number;
  };
  profit: {
    monthly: number;
    annual: number;
  };
  balanceDaysNegative: {
    balance: number;
    daysNegative: number;
  };
  nonTrueRevenue: {
    monthly: number;
    annual: number;
  };
  lowDays: number | null;
  trueBalance: number | null;
  dayswithReturns: number | null;
  mCAWithholdPercent: string | null;
  combinedDaysNegative: number | null;
  averageMonthlyNetRevenue: {
    monthly: number;
    annual: number;
  };
  averageMonthlyFactoringRevenue: {
    monthly: number;
    annual: string;
  };
  minimumMonthlyTrueRevenue: number | null;
  averageMonthlyCreditCardRevenue: {
    monthly: number | null;
    annual: string;
  };
};

export type ApplicationDto = {
  app_key: string;
  user: T_MongoId;
  loans: T_MongoId[];
  loan_lock?: LOAN_LOCK_FLAGS | "";
  banks: T_MongoId[];
  statement_files?: T_MongoId[];
  selected_bank?: SelectedBank;
  lawsuits: T_MongoId[];
  lawsuit_insurance?: boolean;
  agreements: T_MongoId[];
  dms_id?: string;
  dms_customer_vault_id?: string;
  external_links: {
    mt_app_number?: number;
    pandadoc?: {
      name: string;
      doc_id: string;
      status: string;
      regeneration_count: number;
      // strategy_used: string;
      weekly_payments: number;
      session_id?: string;
      expires_at?: string;
      // sent_link?: string;
      recipients: {
        id?: string;
        email: string;
        first_name: string;
        last_name: string;
        role: "Client" | "Cosigner";
        sent_link?: string;
      }[];
    }[];
  };
  client_details: ClientDetails;
  business_info: BusinessInfo;
  accounts_connected: {
    bank_name: string;
    bank_link: any;
    meta: any;
    account_number: string;
  }[];
  summary: {
    current_debt: number;
    weekly_payments: number;
    monthly_revenue?: number;
    monthly_profit?: number;
    money_thumb?: {
      metrics: MetricDocument;
      // additional fields
      accountslist?: [];
      thumbprints?: [];
      monthlymca?: [];
      mcacompanies?: [];
      reptrans?: [];
      mcatrans?: [];
      nsftrans?: [];
      overtrans?: [];
      dailybalance?: [];
      dailycashflow?: [];
      monthlycashflow?: [];
      credittrans?: [];
      truecredittrans?: [];
      untruecredittrans?: [];
      xferitrans?: [];
      xferotrans?: [];
      largetrans?: [];
      returnedtrans?: [];
      monthlynegativedays?: [];
      mobilepayments?: [];
      investments?: [];
      monthlyrecovery?: [];
      casinotrans?: [];
      // anything else
      meta?: any;
    };
    total_advance_amount: number;
    transaction_data?: any;
  };
  dms_response?: any;
  status: {
    last_step: string;
    onboarding: boolean;
    loans: boolean;
    lawsuits: boolean;
    banks: boolean;
    agreements: boolean;
    accounts_connected: boolean;
    strategy: boolean;
    signatures: boolean;
    personal_info: boolean;
    advance_paid: boolean;
  };
  payment_status: {
    initial_paid: PAYMENT_STATUS;
    initial_method?: string;
    service_initiated: PAYMENT_STATUS;
    service_method?: string;
    program_initiated: PAYMENT_STATUS;
    program_method?: string;
  };
  strategy?: {
    custom_payments: boolean;
    plan: string;
    initial_payment: number;
    weekly_payments: number;
    interest?: number;
    interest_rate?: number;
    service_weekly?: number;
    tenure: number;
    program_tenure: number;
    lawsuit_initial_payment: number;
    lawsuit_monthly_payment: number;
    extras?: {
      is_custom_divided: boolean;
      strategy_clause: number;
      split_rows?: any;
      is_initial_split: boolean;
      initial_amount?: number;
      initial_split?: number;
      split_at: number;
    };
  };
  managers_approval?: {
    budget: number;
    last_updated: string;
    status: MANAGERS_APPROVAL_STATUS;
  };
  tables?: {
    enrolled_creditors?: any;
    program_summary?: any;
    payment_schedule?: {
      first_row: PaymentRow;
      headers: ("week" | "payment" | "program_fee" | "service_fee" | "legal_fee" | "creditor")[];
      lawsuit: boolean;
      repeat_rows: {
        count: number;
        row: PaymentRow;
      }[];
      totals: PaymentRow;
      weeks: number;
    };
  };
  messages?: T_MongoId[];
  referrer_code?: string;
  temporary_attachments?: T_MongoId[];
  ai_responses?: any;
  graphs?: any;
  org_code?: ORGANISATIONS | string;
  payment_methods?: {
    name?: string;
    amount?: number;
    start_date?: Date;
    method_type: "seamless" | "easypay" | "actum" | "pg"; // pg for general payment gateway
  }[];
  payment_tokens?: {
    actum?: string;
    seamless?: string;
    easypay?: string;
  };
  application_type: ApplicationType;
  plaid_config?: {
    user_id?: string;
    access_token?: string;
    connected?: boolean;
  };
  viewers?: string[];
};

type _Status = keyof Omit<ApplicationDto["status"], "last_step">;
export const APPLICATION_STATUS = {
  ONBOARDING: "onboarding",
  LOANS: "loans",
  BANKS: "banks",
  LAWSUITS: "lawsuits",
  AGREEMENTS: "agreements",
  ADVANCE_PAID: "advance_paid",
  ACCOUNTS_CONNECTED: "accounts_connected",
  STRATEGY: "strategy",
  PERSONAL_INFO: "personal_info",
  SIGNATURES: "signatures",
} satisfies { [K in Uppercase<_Status>]: Lowercase<K> };

export type ExternalPartnerData = {
  name: string;
  email?: string;
  phone?: string;
  application: {
    summary: {
      current_debt?: number;
      weekly_payments?: number;
      monthly_revenue?: number;
    };
    lawsuits: boolean;
  };
  extra?: {
    cash_needed?: number;
    browser_location?: string;
  };
};

export type ApplicationSchemaDto = ApplicationDto & T_MongoDoc;
export type CreateApplicationDto = ApplicationDto;
export type UpdateApplicationDto = Partial<ApplicationSchemaDto>;

export type PaymentRow = {
  week: string;
  payment: number;
  program_fee: number;
  service_fee: number;
  legal_fee: number;
  creditor: number;
};
